import { listWebTabs, createWebTab, updateWebTab, deleteWebTab, getWebTab } from "../../api/webtabs";

// URL generate helper fonksiyonu
function buildWebTabUrl(webTab, user, site, token, webApps, contact) {
    if (!webTab) return "";

    let baseUrl = "";

    if (webTab.type === "url") {
        baseUrl = webTab.url || "";
    } else if (webTab.type === "app" && webTab.appId) {
        // Web App'i bul
        const app = webApps.find((a) => a._id === webTab.appId);
        if (app && app.slug) {
            baseUrl = `https://${app.slug}.webapp.supsis.ai`;
        }
    }

    if (!baseUrl) return "";

    // Query parametrelerini ekle
    const queryParams = webTab.queryParams || [];
    if (queryParams.length === 0) {
        return baseUrl;
    }

    // Dinamik değişken map'i - User parametreleri
    const variableMap = {
        "user.name": user?.name || "",
        "user.email": user?.email || "",
        "user.id": user?._id || "",
        "user.role": user?.role?.name || "",
        "user.lang": user?.lang || "",
        "user.phone": user?.phone || "",
        "user.status": user?.status || "",
        "site.id": site?._id || "",
        "user.token": token || "",
        // Contact/Visitor parametreleri
        "contact.id": contact?._id || "",
        "contact.fullname": contact?.fullname || "",
        "contact.email": contact?.email || "",
        "contact.phone": contact?.phone || "",
        "contact.whatsappNumber": contact?.whatsappNumber || "",
        "contact.platform": contact?.platform || "",
        "contact.channel": contact?.channel || "",
        "contact.preferredLang": contact?.preferredLang || "",
        "contact.country": contact?.country || "",
        "contact.city": contact?.city || "",
        "contact.ipAddress": contact?.ipAddress || "",
        "contact.visitingPage": contact?.visitingPage || "",
        "contact.referer": contact?.referer || "",
        "contact.os": contact?.os || "",
        "contact.browser": contact?.browser || "",
        "contact.vipCustomer": contact?.vipCustomer ? "true" : "false",
        "contact.answerStatus": contact?.answerStatus || "",
        "contact.owner": contact?.owner || "",
        "contact.instagramUserId": contact?.instagramUserId || "",
        "contact.telegramUserId": contact?.telegramUserId || "",
        "contact.messengerUserId": contact?.messengerUserId || "",
        "contact.metaLeadId": contact?.metaLeadId || "",
        "contact.linkedinUserId": contact?.linkedinUserId || "",
        "contact.zohoContactId": contact?.zohoInfo?.contactId || "",
        "contact.zohoLeadId": contact?.zohoInfo?.leadId || "",
        "contact.flowluAccountId": contact?.flowluAccountId || "",
        "contact.bitrixContactId": contact?.bitrixContactId || "",
        "contact.tags": Array.isArray(contact?.tags) ? contact.tags.map((t) => t._id || t).join(",") : "",
    };

    const params = new URLSearchParams();

    queryParams.forEach((param) => {
        if (!param.key) return;

        let value = param.value || "";

        // Dinamik değişkenleri replace et
        if (param.isDynamic && value) {
            const matches = value.match(/\$\{([^}]+)\}/g);
            if (matches) {
                matches.forEach((match) => {
                    const key = match.replace(/\$\{|\}/g, "");
                    // variableMap'te varsa replace et, yoksa olduğu gibi bırak (context'te çözümlenecek)
                    if (variableMap.hasOwnProperty(key)) {
                        value = value.replace(match, variableMap[key]);
                    }
                    // else: placeholder olduğu gibi kalır, WebTabViewer'da context'e göre çözümlenir
                });
            }
        }

        params.append(param.key, value);
    });

    const queryString = params.toString();
    if (queryString) {
        const separator = baseUrl.includes("?") ? "&" : "?";
        return `${baseUrl}${separator}${queryString}`;
    }

    return baseUrl;
}

const webtabs = {
    namespaced: true,
    state: {
        webTabs: [],
        loading: false,
    },
    getters: {
        getVisibleWebTabs: (state, getters, rootState) => {
            const user = rootState.auth.user;
            const adminUser = rootState.auth.adminUser;
            const isRoot = user?.role?.isRoot || false;

            // Admin veya root kullanıcı hepsini görebilir
            if (adminUser || isRoot) {
                return state.webTabs.filter((tab) => tab.enabled);
            }

            const userId = user?._id;
            const userDepartments = user?.subjects || [];

            return state.webTabs.filter((tab) => {
                if (!tab.enabled) return false;

                const visibility = tab.visibility || {};
                const visibilityUsers = visibility.users || [];
                const visibilityDepartments = visibility.departments || [];
                const visibilityOwners = visibility.owners || [];

                // Hiç visibility yoksa herkese açık
                const isPublic = visibilityUsers.length === 0 && visibilityDepartments.length === 0;

                // Owner kontrolü
                const isOwner = visibilityOwners.includes(userId);

                // User kontrolü
                const hasUserAccess = visibilityUsers.includes(userId);

                // Department kontrolü
                const hasDepartmentAccess =
                    userDepartments.some((department) => visibilityDepartments.includes(department)) ||
                    userDepartments.length === 0;

                return isPublic || isOwner || hasUserAccess || hasDepartmentAccess;
            });
        },
        getMenuWebTabs: (state, getters) => {
            return getters.getVisibleWebTabs.filter((tab) => tab.placement === "menu" || !tab.placement);
        },
        getToolbarWebTabs: (state, getters) => {
            return getters.getVisibleWebTabs
                .filter((tab) => tab.placement === "toolbar")
                .sort((a, b) => (a.order || 0) - (b.order || 0));
        },
        // Contact Widget placement'ı için webtab'lar
        getContactWidgetWebTabs: (state, getters) => {
            return getters.getVisibleWebTabs
                .filter((tab) => tab.placement === "contact-widget")
                .sort((a, b) => (a.order || 0) - (b.order || 0));
        },
        // Visitor Detail placement'ı için webtab'lar
        getVisitorDetailWebTabs: (state, getters) => {
            return getters.getVisibleWebTabs
                .filter((tab) => tab.placement === "visitor-detail")
                .sort((a, b) => (a.order || 0) - (b.order || 0));
        },
        // App Switcher placement'ı için webtab'lar
        getAppSwitcherWebTabs: (state, getters) => {
            return getters.getVisibleWebTabs
                .filter((tab) => tab.placement === "app-switcher")
                .sort((a, b) => (a.order || 0) - (b.order || 0));
        },
        // Table Record Tab placement'ı için webtab'lar
        getTableRecordWebTabs: (state, getters) => {
            return getters.getVisibleWebTabs
                .filter((tab) => tab.placement === "table-record-tab")
                .sort((a, b) => (a.order || 0) - (b.order || 0));
        },
        // Task Detail Tab placement'ı için webtab'lar
        getTaskDetailWebTabs: (state, getters) => {
            return getters.getVisibleWebTabs
                .filter((tab) => tab.placement === "task-detail-tab")
                .sort((a, b) => (a.order || 0) - (b.order || 0));
        },
        // Placement bazlı genel getter
        webTabsByPlacement: (state, getters) => (placement) => {
            return getters.getVisibleWebTabs
                .filter((tab) => tab.placement === placement)
                .sort((a, b) => (a.order || 0) - (b.order || 0));
        },
        // Belirli bir tablo için uygun table-record-tab WebTab'ları
        getTableRecordWebTabsForTable: (state, getters) => (tableId) => {
            return getters.getTableRecordWebTabs.filter((tab) => {
                var config = tab.tableRecordConfig || {};
                // Kapsam "all" ise veya scope yoksa herkese göster
                if (!config.scope || config.scope === "all") return true;
                // Specific scope ise tableId eşleşmeli
                return config.tableId === tableId;
            });
        },
        // WebTab URL'sini generate eden getter (webTab objesi ve opsiyonel contact parametre olarak alır)
        getWebTabUrl: (state, getters, rootState) => (webTab, contact) => {
            const user = rootState.auth.user;
            const site = rootState.site.site;
            const token = rootState.auth.token;
            const webApps = rootState.webapps?.webApps || [];
            return buildWebTabUrl(webTab, user, site, token, webApps, contact);
        },
    },
    mutations: {
        SET_WEB_TABS(state, webTabs) {
            state.webTabs = webTabs;
        },
        SET_LOADING(state, loading) {
            state.loading = loading;
        },
        ADD_WEB_TAB(state, webTab) {
            state.webTabs.push(webTab);
        },
        UPDATE_WEB_TAB(state, updatedWebTab) {
            const index = state.webTabs.findIndex((tab) => tab._id === updatedWebTab._id);
            if (index !== -1) {
                state.webTabs.splice(index, 1, updatedWebTab);
            }
        },
        REMOVE_WEB_TAB(state, webTabId) {
            state.webTabs = state.webTabs.filter((tab) => tab._id !== webTabId);
        },
    },
    actions: {
        async fetchWebTabs({ commit }) {
            try {
                commit("SET_LOADING", true);
                const response = await listWebTabs();
                commit("SET_WEB_TABS", response.data || response);
                return response.data || response;
            } catch (error) {
                console.error("Error fetching web tabs:", error);
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },
        async createWebTab({ commit }, webTabData) {
            try {
                const response = await createWebTab(webTabData);
                commit("ADD_WEB_TAB", response.data || response);
                return response.data || response;
            } catch (error) {
                console.error("Error creating web tab:", error);
                throw error;
            }
        },
        async updateWebTab({ commit }, { id, data }) {
            try {
                const response = await updateWebTab(id, data);
                commit("UPDATE_WEB_TAB", response.data || response);
                return response.data || response;
            } catch (error) {
                console.error("Error updating web tab:", error);
                throw error;
            }
        },
        async deleteWebTab({ commit }, webTabId) {
            try {
                await deleteWebTab(webTabId);
                commit("REMOVE_WEB_TAB", webTabId);
                return true;
            } catch (error) {
                console.error("Error deleting web tab:", error);
                throw error;
            }
        },
        async getWebTab({ commit }, webTabId) {
            try {
                const response = await getWebTab(webTabId);
                return response.data || response;
            } catch (error) {
                console.error("Error getting web tab:", error);
                throw error;
            }
        },
    },
};

export default webtabs;
